import { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { serverInfo } from '../config/serverConfig';
import { getAuthHeaders } from '../api/authHeaders';

const DEFAULT_START = '08:00';
const DEFAULT_END = '17:00';

export function userHasWorkingHoursOverride(userInfo) {
  if (!userInfo) return false;
  const s = userInfo.working_hours_start != null && String(userInfo.working_hours_start).trim() !== '';
  const e = userInfo.working_hours_end != null && String(userInfo.working_hours_end).trim() !== '';
  return s && e;
}

/** Parse HH:mm to hour (0-23). */
function parseHour(hhmm) {
  if (!hhmm || typeof hhmm !== 'string') return null;
  const part = hhmm.trim().split(':')[0];
  const h = parseInt(part, 10);
  if (Number.isNaN(h) || h < 0 || h > 23) return null;
  return h;
}

/**
 * Fetches current account's working hours (working_hours_start, working_hours_end).
 * Returns defaults 8am–5pm when not set or on error.
 * @returns {{ workingHoursStart: string, workingHoursEnd: string, workingHoursStartHour: number, workingHoursEndHour: number, accountTimeZone: string, loading: boolean, error: string }}
 */
export function useAccountWorkingHours() {
  const { userInfo } = useAuth();
  const accountId = userInfo?.account_id;
  const [start, setStart] = useState(DEFAULT_START);
  const [end, setEnd] = useState(DEFAULT_END);
  const [accountTimeZone, setAccountTimeZone] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!accountId) {
      setStart(DEFAULT_START);
      setEnd(DEFAULT_END);
      setAccountTimeZone('');
      setLoading(false);
      setError('');
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError('');
    getAuthHeaders()
      .then((headers) =>
        fetch(serverInfo.serverUrl + '/getAccount', {
          method: 'POST',
          headers,
          body: JSON.stringify({ account_id: accountId }),
        })
      )
      .then((resp) => resp.json())
      .then((data) => {
        if (cancelled) return;
        if (data && data.success === false) {
          throw new Error(data.message || 'Failed to load working hours');
        }
        const list = Array.isArray(data) ? data : [];
        const account = list.find((a) => String(a._id) === String(accountId)) || list[0];
        const whStart = (account?.working_hours_start != null && String(account.working_hours_start).trim() !== '')
          ? String(account.working_hours_start).trim()
          : DEFAULT_START;
        const whEnd = (account?.working_hours_end != null && String(account.working_hours_end).trim() !== '')
          ? String(account.working_hours_end).trim()
          : DEFAULT_END;
        setStart(whStart);
        setEnd(whEnd);
        const tz =
          account?.time_zone != null && String(account.time_zone).trim() !== ''
            ? String(account.time_zone).trim()
            : '';
        setAccountTimeZone(tz);
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err?.message || 'Failed to load working hours');
          setStart(DEFAULT_START);
          setEnd(DEFAULT_END);
          setAccountTimeZone('');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [accountId]);

  const startHour = parseHour(start);
  const endHour = parseHour(end);

  return {
    workingHoursStart: start,
    workingHoursEnd: end,
    workingHoursStartHour: startHour != null ? startHour : 8,
    workingHoursEndHour: endHour != null ? endHour : 17,
    /** Account IANA time zone (events / org default); empty if unset. */
    accountTimeZone: accountTimeZone,
    loading,
    error,
  };
}

/**
 * Account working hours merged with the signed-in user's optional override (for tasks, events, time blocking, auto-schedule).
 */
export function useEffectiveWorkingHours() {
  const account = useAccountWorkingHours();
  const { userInfo } = useAuth();
  const override = userHasWorkingHoursOverride(userInfo);
  const start = override
    ? String(userInfo.working_hours_start).trim()
    : account.workingHoursStart;
  const end = override
    ? String(userInfo.working_hours_end).trim()
    : account.workingHoursEnd;
  const startHour = parseHour(start);
  const endHour = parseHour(end);
  return {
    ...account,
    workingHoursStart: start,
    workingHoursEnd: end,
    workingHoursStartHour: startHour != null ? startHour : 8,
    workingHoursEndHour: endHour != null ? endHour : 17,
    usesUserOverride: override,
    userWorkingHoursTimeZone:
      override && userInfo?.working_hours_time_zone != null && String(userInfo.working_hours_time_zone).trim() !== ''
        ? String(userInfo.working_hours_time_zone).trim()
        : null,
  };
}
